/* OS EDD - Top Shop Multi (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-os-edd-top-shop-multi]';
  const SELECTOR_CTA = '[data-osedd-cta]';
  const SELECTOR_STICKY = '[data-osedd-sticky]';
  const SELECTOR_STICKY_CTA = '[data-osedd-sticky-cta]';
  const CHECKOUT_REDIRECT_FLAG = '__oseddCheckoutRedirectUntil';

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];
    // Accept comma or newline separated
    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function getSectionConfig(root) {
    return {
      bogoUrlParamsEnabled: root && root.dataset.bogoUrlParamsEnabled === 'true',
      bogoUrlParams: (root && root.dataset.bogoUrlParams) || '',
      checkoutUrl: (root && root.dataset.checkoutUrl) || '/checkout',
      giftCardAttrSubEnabled: root && root.dataset.giftCardAttrSubEnabled === 'true',
      giftCardAttrOtpEnabled: root && root.dataset.giftCardAttrOtpEnabled === 'true',
      giftCardAmount: (root && root.dataset.giftCardAttrAmount) || '',
    };
  }

  function getPromoGiftItems(root) {
    if (!root || root.dataset.customPromoEnabled !== 'true') return [];
    const landingSource = (root.dataset.customPromoLandingSource || '').trim();
    const rawIds = (root.dataset.customPromoGiftIds || '').trim();
    if (!landingSource || !rawIds) return [];
    return rawIds.split(',').map((s) => s.trim()).filter(Boolean).map((id) => ({
      id: Number(id),
      quantity: 1,
      properties: {
        _landing_source: landingSource,
        __landing_source: landingSource,
        _promo_gift: 'true',
      },
    }));
  }

  function attachPromoLandingSource(item, root) {
    if (!root || root.dataset.customPromoEnabled !== 'true') return item;
    const landingSource = (root.dataset.customPromoLandingSource || '').trim();
    if (!landingSource) return item;
    return Object.assign({}, item, {
      properties: Object.assign({}, item.properties || {}, {
        _landing_source: landingSource,
        __landing_source: landingSource,
      }),
    });
  }

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';
    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.indexOf('://') !== -1) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function applyBogoUrlParams(root, options) {
    const sectionConfig = getSectionConfig(root);
    const bogo = window.AmbrosiaBogoUrlParams;
    if (!bogo) return false;
    return bogo.apply(sectionConfig, options);
  }

  function extractBogoCartAttributes(root) {
    const sectionConfig = getSectionConfig(root);
    const raw = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!raw) return null;
    const attrs = {};
    let hasAny = false;
    new URLSearchParams(raw).forEach((value, key) => {
      if (!key) return;
      attrs[key] = value;
      hasAny = true;
    });
    return hasAny ? attrs : null;
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      function check() {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            if (cart.item_count > knownItemCount) {
              resolve(true);
            } else {
              elapsed += interval;
              if (elapsed >= maxWait) {
                resolve(false);
              } else {
                setTimeout(check, interval);
              }
            }
          })
          .catch(() => resolve(false));
      }

      setTimeout(check, interval);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const sectionConfig = getSectionConfig(root);
    if (!sectionConfig.bogoUrlParamsEnabled) return;

    const sanitizedParams = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!sanitizedParams) return;

    const incomingParams = new URLSearchParams(sanitizedParams);
    incomingParams.forEach((value, key) => {
      if (!key) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getCheckoutUrlWithCurrentParams(root, discountCodes) {
    const sectionConfig = getSectionConfig(root);
    const checkoutBase = sectionConfig.checkoutUrl || '/checkout';
    const checkoutUrl = new URL(checkoutBase, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      checkoutUrl.searchParams.set(key, value);
    });

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }
    return checkoutUrl.toString();
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-osedd-status]');
    if (!el) return;
    el.textContent = msg || '';
  }

  function getScrollOffsetPx() {
    // If your theme header is visible/sticky, account for it.
    // If header is hidden (like your CSS), this will end up 0.
    try {
      const header = document.querySelector('.header-wrapper');
      if (!header) return 0;

      const style = window.getComputedStyle(header);
      if (style.display === 'none' || style.visibility === 'hidden') return 0;

      // Only offset if it’s fixed/sticky (common for Shopify headers)
      const pos = (style.position || '').toLowerCase();
      if (pos !== 'fixed' && pos !== 'sticky') return 0;

      const h = Math.round(header.getBoundingClientRect().height || 0);
      return Math.max(0, h);
    } catch (e) {
      return 0;
    }
  }

  function smoothScrollToTarget(targetEl) {
    if (!targetEl) return;

    const offset = getScrollOffsetPx();
    const rect = targetEl.getBoundingClientRect();
    const y = Math.round(window.pageYOffset + rect.top - offset - 12); // 12px breathing room

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  function bindInfoDropdowns(root) {
    const items = root.querySelectorAll('.osedd-topshop__infoItem');
    if (!items || !items.length) return;

    items.forEach((item) => {
      const btn = item.querySelector('[data-osedd-info-btn]');
      const panel = item.querySelector('[data-osedd-info-panel]');
      if (!btn || !panel) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all siblings (accordion behavior)
        items.forEach((sib) => {
          if (sib === item) return;
          sib.classList.remove('is-open');
          const sibBtn = sib.querySelector('[data-osedd-info-btn]');
          const sibPanel = sib.querySelector('[data-osedd-info-panel]');
          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');
          if (sibPanel) sibPanel.hidden = true;
        });

        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });
  }

  function bindRteScrollLinks(root) {
    // Only handle anchor links inside this section’s RTE areas
    const rteContainers = root.querySelectorAll(
      '.osedd-topshop__awardBody.rte, .osedd-topshop__offerBody.rte, .osedd-topshop__awardBodyImgRight.rte'
    );
    if (!rteContainers || !rteContainers.length) return;

    rteContainers.forEach((wrap) => {
      wrap.addEventListener('click', (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a) return;

        const href = (a.getAttribute('href') || '').trim();
        if (!href || href === '#') return;

        // Only intercept same-page hash links (e.g. #reviews)
        if (href.charAt(0) !== '#') return;

        const id = href.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();
        smoothScrollToTarget(target);

        // Optional: keep URL hash in sync without jumping
        try {
          history.pushState(null, '', `#${id}`);
        } catch (err) {}
      });
    });
  }

  function setStickyVisible(root, isVisible) {
    if (!root) return;

    const sticky = root.querySelector(SELECTOR_STICKY);
    if (!sticky) return;

    if (isVisible) {
      root.classList.add('osedd-sticky-active');
      sticky.setAttribute('aria-hidden', 'false');
    } else {
      root.classList.remove('osedd-sticky-active');
      sticky.setAttribute('aria-hidden', 'true');
    }
  }

  /** When multiple os-shop sections want the sticky bar, pick one winner (single fixed bar on the page). */
  function pickStickyWinner(roots) {
    if (!roots || !roots.length) return null;
    if (roots.length === 1) return roots[0];

    const vh = window.innerHeight || 0;
    let best = null;
    let bestScore = -1;

    for (const root of roots) {
      const r = root.getBoundingClientRect();
      const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      const score = Math.max(0, visible);
      if (score > bestScore) {
        bestScore = score;
        best = root;
      }
    }

    if (bestScore > 0) return best;
    return roots[roots.length - 1];
  }

  let stickyGlobalRaf = null;

  function applyGlobalSticky() {
    const roots = Array.from(document.querySelectorAll(SELECTOR_ROOT));
    const want = roots.filter(
      (r) => r.__oseddStickyWantsOn && r.querySelector(SELECTOR_STICKY) && hasMainShopPassed()
    );

    let winner = null;
    if (want.length === 1) winner = want[0];
    else if (want.length > 1) winner = pickStickyWinner(want);

    roots.forEach((r) => {
      if (!r.querySelector(SELECTOR_STICKY)) return;
      setStickyVisible(r, winner !== null && r === winner);
    });
  }

  function scheduleApplyGlobalSticky() {
    if (stickyGlobalRaf != null) return;
    stickyGlobalRaf = requestAnimationFrame(() => {
      stickyGlobalRaf = null;
      applyGlobalSticky();
    });
  }

  function ensureGlobalStickyScrollSync() {
    window.__oseddStickyAppliers = window.__oseddStickyAppliers || [];
    if (!window.__oseddStickyAppliers.includes(scheduleApplyGlobalSticky)) {
      window.__oseddStickyAppliers.push(scheduleApplyGlobalSticky);
    }
    if (window.__oseddStickyGlobalScrollBound) return;
    window.__oseddStickyGlobalScrollBound = true;
    const runAll = () => {
      (window.__oseddStickyAppliers || []).forEach((fn) => fn());
    };
    window.addEventListener('scroll', runAll, { passive: true });
    window.addEventListener('resize', runAll);
  }

  function hasMainShopPassed() {
    const mainShop = document.querySelector('.mgn-main-shop');
    if (!mainShop) return true;
    return mainShop.getBoundingClientRect().bottom <= 0;
  }

  function setStickyWant(root, wantsSticky) {
    if (!root) return;
    root.__oseddStickyWantsOn = !!wantsSticky;
    applyGlobalSticky();
  }

  function shouldWantSticky(mainCta) {
    if (!mainCta) return false;
    // Do not show while still above the offer — only after the main CTA has scrolled past
    return mainCta.getBoundingClientRect().bottom <= 0;
  }

  function bindStickyObserver(root, mainCta) {
    if (!root || !mainCta) return;

    // Avoid double-binding observers
    if (root.__oseddStickyObserved) return;
    root.__oseddStickyObserved = true;

    ensureGlobalStickyScrollSync();

    const updateStickyWant = () => {
      setStickyWant(root, shouldWantSticky(mainCta));
    };

    window.addEventListener('scroll', updateStickyWant, { passive: true });
    window.addEventListener('resize', updateStickyWant);

    // Fallback if IO not supported
    if (!('IntersectionObserver' in window)) {
      updateStickyWant();
      return;
    }

    const obs = new IntersectionObserver(
      () => {
        updateStickyWant();
      },
      {
        root: null,
        threshold: [0, 0.25, 0.5, 1],
      }
    );

    obs.observe(mainCta);
    root.__oseddStickyObs = obs;
    updateStickyWant();
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;
    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) btn.classList.add('is-busy');
    else btn.classList.remove('is-busy');
  }

  function setCheckoutRedirectGuard(durationMs = 6000) {
    window[CHECKOUT_REDIRECT_FLAG] = Date.now() + Math.max(1000, Number(durationMs) || 6000);
  }

  function clearCheckoutRedirectGuard() {
    delete window[CHECKOUT_REDIRECT_FLAG];
  }

  function buildCartItemFromAttrs(btn, variantAttr, qtyAttr, sellingPlanAttr) {
    const variantIdRaw = (btn.getAttribute(variantAttr) || '').trim();
    const qty = Number(btn.getAttribute(qtyAttr) || 1);
    const sellingPlanRaw = (btn.getAttribute(sellingPlanAttr) || '').trim();
    const variantId = Number(variantIdRaw || 0);
    const sellingPlanId = Number(sellingPlanRaw || 0);

    if (!variantId) return null;

    const item = {
      id: variantId,
      quantity: Math.max(1, qty || 1),
    };

    if (sellingPlanRaw && sellingPlanId > 0) {
      item.selling_plan = sellingPlanId;
    }

    return item;
  }

  async function handleCheckoutClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (root.__oseddCheckoutSubmitting) return;
    root.__oseddCheckoutSubmitting = true;

    const mainCta = root.querySelector(SELECTOR_CTA);
    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);

    setStatus(root, '');
    setBusy(mainCta, true);
    setBusy(stickyCta, true);

    try {
      if (!window.AmbrosiaFastCheckout) {
        throw new Error('Checkout helper failed to load. Please refresh and try again.');
      }

      const clearCart = btn.getAttribute('data-osedd-clear-cart') === '1';

      const mainItem = buildCartItemFromAttrs(
        btn,
        'data-osedd-main-variant',
        'data-osedd-main-qty',
        'data-osedd-main-selling-plan'
      );

      if (!mainItem) {
        throw new Error('Missing main variant ID. Please set it in the section settings.');
      }

      const secondaryItem = buildCartItemFromAttrs(
        btn,
        'data-osedd-secondary-variant',
        'data-osedd-secondary-qty',
        'data-osedd-secondary-selling-plan'
      );

      const thirdItem = buildCartItemFromAttrs(
        btn,
        'data-osedd-third-variant',
        'data-osedd-third-qty',
        'data-osedd-third-selling-plan'
      );

      let freebies = [];
      const freebiesRaw = btn.getAttribute('data-osedd-freebies') || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (e) {
        freebies = [];
      }

      const offerItems = [mainItem, secondaryItem, thirdItem]
        .filter(Boolean)
        .map((item) => attachPromoLandingSource(item, root));

      const freebieItems = (freebies || [])
        .filter((it) => it && Number(it.id) > 0)
        .map((it) => ({
          id: Number(it.id),
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));

      const promoGiftItems = getPromoGiftItems(root);
      const sectionConfig = getSectionConfig(root);
      await window.AmbrosiaFastCheckout.addThenCheckout({
        sectionConfig: sectionConfig,
        clearCart: clearCart,
        items: offerItems.concat(freebieItems).concat(promoGiftItems),
        fallbackItems: offerItems,
        waitForGift: sectionConfig.bogoUrlParamsEnabled,
        discountCodes: parseDiscountCodes(btn.getAttribute('data-osedd-discounts')),
      });
      return;
    } catch (err) {
      const msg = err && err.message ? err.message : 'Unable to start checkout. Please try again.';
      setStatus(root, msg);
      setBusy(mainCta, false);
      setBusy(stickyCta, false);
      root.__oseddCheckoutSubmitting = false;
      if (window.AmbrosiaFastCheckout) window.AmbrosiaFastCheckout.endRedirecting();
    }
  }

  function bindRoot(root) {
    if (!root || root.__oseddBound) return;
    root.__oseddBound = true;

    if (applyBogoUrlParams(root, { allowReload: true })) return;

    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.addEventListener('click', () => handleCheckoutClick(cta));
    }

    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);
    if (stickyCta) {
      stickyCta.addEventListener('click', () => handleCheckoutClick(stickyCta));
    }

    // Show sticky only when this section renders the bar (e.g. sticky disabled in theme editor)
    if (cta && root.querySelector(SELECTOR_STICKY)) {
      bindStickyObserver(root, cta);
    }

    // Dropdown info accordions (desktop + mobile placements)
    bindInfoDropdowns(root);

    // Smooth-scroll for hash links inside RTE content (award/offer richtext)
    bindRteScrollLinks(root);

    // Gallery: main image switch + thumb strip with side arrows
    const gallery = root.querySelector('[data-osedd-gallery]');
    const mainSlides = root.querySelectorAll('[data-osedd-gal-main-slide]');
    const thumbs = root.querySelectorAll('[data-osedd-thumb]');
    const thumbViewport = root.querySelector('[data-osedd-thumb-viewport]');
    const thumbList = root.querySelector('[data-osedd-thumb-list]');
    const mainPrev = root.querySelector('[data-osedd-main-nav="prev"]');
    const mainNext = root.querySelector('[data-osedd-main-nav="next"]');
    // If thumbs exist, move the main-nav buttons down to sit beside the thumbs row (CSS handles positioning)
    if (root.querySelector('.osedd-topshop__galThumbs')) {
      root.classList.add('osedd-has-thumbs');
    } else {
      root.classList.remove('osedd-has-thumbs');
    }

    if (gallery && mainSlides.length && thumbs.length) {
      let index = 0;
      const syncMainNav = () => {
        if (!mainPrev || !mainNext) return;
        const max = Math.max(0, mainSlides.length - 1);
        mainPrev.disabled = index <= 0;
        mainNext.disabled = index >= max;
      };

      const setActive = (nextIndex) => {
        const max = Math.max(0, mainSlides.length - 1);
        index = Math.min(Math.max(nextIndex, 0), max);

        mainSlides.forEach((s, i) => {
          const isActive = i === index;
          s.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) s.setAttribute('data-osedd-active', '1');
          else s.removeAttribute('data-osedd-active');

          s.querySelectorAll('video').forEach((video) => {
            if (!isActive && typeof video.pause === 'function') video.pause();
          });
        });

        thumbs.forEach((t, i) => {
          const isActive = i === index;
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) t.setAttribute('data-osedd-active', '1');
          else t.removeAttribute('data-osedd-active');
        });

        // Keep active thumb in view (NO centering -> prevents half-cut thumbs)
        const activeThumb = thumbs[index];
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
        syncMainNav();
      };

      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const i = Number(t.getAttribute('data-osedd-gal-index') || 0);
          setActive(i);
        });
      });

      if (mainPrev) {
        mainPrev.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index - 1);
        });
      }

      if (mainNext) {
        mainNext.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index + 1);
        });
      }
      // Keep thumbs usable with native horizontal scrolling
      if (thumbViewport) {
        thumbViewport.style.scrollBehavior = 'smooth';
      }
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();